import { Injectable } from '@angular/core';
import { CommonUtilty } from '../../utilities/commonUtility';
import { Validators, UntypedFormControl, AbstractControl, FormControl, FormGroup } from '@angular/forms';
import { CustomValidators } from 'ngx-custom-validators';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { ErrorDO } from 'app/BaseFramework/shared/models/error.model';
import { ApiRequestService } from '../../http';
import { environment } from 'environments/environment';
import { AppMetadataHandler } from 'app/BaseFramework/configs';
import { CustomValidatorsService } from './customValidators.service';
import { map } from 'rxjs/operators';
import { catchError } from 'rxjs/operators';
import * as XLSX from 'xlsx';
import { HttpClient } from '@angular/common/http';

export interface NewValidationItem {
  fieldName: string;
  validations: {
    [validationType: string]: {
      enabled: boolean;
      errorMessage: string;
      valueForParameter?: any;
      exceptionCd: any;
      overridable: boolean;
      type: string;
      validationLevel: string;
      sourceFields?: { field: string; triggerValue: any[] }[];
      conditionType?: string;
    };
  };
  type?: string;
  state?: any;
}

interface ValidationOutput {
  isInvalid: boolean;
  errorDOs: Array<ErrorDO>;
}

@Injectable()
export class ValidationUtilService {
  /** 
   * Supported Native Angular Validations:
   * min(), max(), required(), requiredTrue(), email(), 
   * minLength(), maxLength(), pattern()
   * 
   * Supported Custom Validations:
   * array length, base64, credit card, date, date ISO, 
   * digits, email, equal, not equal, equal to, not equal to, 
   * greater than, greater than or equal, json, less than, 
   * less than or equal, max, max date, min, min date, not equal, 
   * not equal to, number, property, range, range length, url, uuid
   */

  // Storage for all validation configurations by form name
  private _allValidations: { [key: string]: NewValidationItem[] } = {};
  
  // Maps source fields to dependent fields for cross-field validation
  private sourceFieldtoMainFieldMap: { [key: string]: string[] } = {};
  
  // Validation schema and language settings
  private validationSchema: any = {};
  private selectedLanguage: string = 'en';
  private errorList: BehaviorSubject<any[]> = new BehaviorSubject<any[]>([]);
  
  // Supported validation types
  validationList: string[] = [
    "required", "requiredTrue", "email", "min", "max", "minlength", "maxlength",
    "pattern", "digits", "number", "url", "date", "dateISO", "creditCard",
    "json", "base64", "minDate", "maxDate", "uuid", "equal", "notEqual",
    "equalTo", "notEqualTo", "arrayLength", "greaterThan", "lessThan",
    "range", "rangeLength"
  ];

  constructor(
    private commonUtilty: CommonUtilty,
    private apiRequest: ApiRequestService,
    private appMetadataHandler: AppMetadataHandler,
    private customValidatorsService: CustomValidatorsService,
    private apiRequestService: ApiRequestService,
    private http: HttpClient
  ) {}
  
  /**
   * Gets the current validation configuration
   * @returns Object containing all validation configurations by form name
   */
  public get allValidations() {
    return this._allValidations;
  }

  /**
   * Sets validation configuration and builds the field dependency map for cross-field validation
   * @param validationList Validation configuration by form name
   */
  public set allValidations(validationList: { [key: string]: NewValidationItem[] }) {
    Object.entries(validationList).forEach(([formName, formFieldValidations]) => {
      formFieldValidations.forEach((fieldValidation: NewValidationItem) => {
        Object.entries(fieldValidation.validations).forEach(([validationType, validationConfig]) => {
          if (validationConfig.sourceFields && validationConfig.sourceFields.length > 0) {
            validationConfig.sourceFields.forEach(field => {
              if (this.sourceFieldtoMainFieldMap[formName + field.field]) {
                this.sourceFieldtoMainFieldMap[formName + field.field].push(fieldValidation.fieldName);
              } else {
                this.sourceFieldtoMainFieldMap[formName + field.field] = [fieldValidation.fieldName];
              }
            });
          }
        });
      });
    });
    this._allValidations = { ...validationList };
  }

  /**
   * Creates a form control with the appropriate validators based on field name and form
   * @param fieldName Name of the field
   * @param formName Name of the form
   * @param value Initial value for the form control (optional)
   * @param state Additional state context (optional)
   * @returns FormControl with configured validators
   */
  setValidationsForFormControl(fieldName, formName, value?, state?) {
    let _listOfValidatorsForFormControl = this.getListOfValidators(state, fieldName, formName);
    if (value !== null && value != undefined)
      return new UntypedFormControl(value, _listOfValidatorsForFormControl);
    else if (_listOfValidatorsForFormControl != null && _listOfValidatorsForFormControl != undefined)
      return new UntypedFormControl(null, _listOfValidatorsForFormControl);
  }

  /**
   * Gets all validators that should be applied to a specific field
   * @param state Additional state context (optional)
   * @param fieldName Name of the field
   * @param formName Name of the form
   * @returns Array of validator functions for the field
   */
  getListOfValidators(state: any, fieldName: any, formName: any) {
    const formValidations = this.allValidations[formName];
    if (!formValidations) return [];
    
    let _filteredValidationItems = formValidations.filter(validation => 
      validation.fieldName === fieldName && validation['validations']);
    let _listOfValidatorsForFormControl = [];
    
    _filteredValidationItems.forEach(validationItem => {
      Object.entries(validationItem.validations).forEach(([validationType, validationConfig]) => {
        if (validationConfig.enabled) {
          let fetchMetaData = this._fetchValidatorObject(validationType, validationConfig.valueForParameter);
          if (fetchMetaData) {
            _listOfValidatorsForFormControl.push(fetchMetaData);
          }
        }
      });
    });
    
    return _listOfValidatorsForFormControl;
  }

  /**
   * Checks if a field has a required validation
   * @param fieldKey Name of the field
   * @param form Name of the form
   * @param state Additional state context (optional)
   * @returns Boolean indicating if the field is required
   */
  isRequired(fieldKey, form?, state?) {
    const formValidations = this.allValidations[form];
    if (!formValidations) return false;
    
    let _filteredValidationItems;
    if (state) {
      _filteredValidationItems = formValidations.filter(
        validation => validation.fieldName === fieldKey && validation.state === state
      );
    } else {
      _filteredValidationItems = formValidations.filter(
        validation => validation.fieldName === fieldKey
      );
    }
    
    return _filteredValidationItems.length > 0 && 
           _filteredValidationItems[0].validations?.required?.enabled;
  }

  getExcelColumnLetter(colNum: number): string {
    let letter = '';
    while (colNum > 0) {
      const mod = (colNum - 1) % 26;
      letter = String.fromCharCode(65 + mod) + letter;
      colNum = Math.floor((colNum - 1) / 26);
    }
    return letter;
  }

  /**
   * Parses an Excel file from binary response data
   * - Converts binary Excel data to JSON format
   * 
   * @param apiUrl URL to fetch the Excel file from
   * @param requestBody Request payload for the API call
   * @returns Observable that emits parsed JSON data from Excel
   */
  parseExcelFromBinaryResponse(apiUrl: string, requestBody: any): Observable<any[]> {
    return this.http.post(apiUrl, requestBody, { 
        responseType: 'arraybuffer',
        headers: {...this.apiRequestService.appendFormHeaderForMultipartNew()}
      }).pipe(
        map(
          (arrayBuffer: ArrayBuffer) => {
              const data = new Uint8Array(arrayBuffer);
              const workbook: XLSX.WorkBook = XLSX.read(data, { type: 'array' });
              const sheetName = workbook.SheetNames[0];
              const worksheet = workbook.Sheets[sheetName];
              // Get all cell addresses (excluding metadata like "!ref")
              const cellAddresses = Object.keys(worksheet).filter(addr => !addr.startsWith('!'));

              // Track max row and column
              let maxRow = 0;
              let maxCol = 0;

              for (const addr of cellAddresses) {
                const { r, c } = XLSX.utils.decode_cell(addr);
                if (worksheet[addr].v !== undefined && worksheet[addr].v !== null && worksheet[addr].v !== '') {
                  maxRow = Math.max(maxRow, r);
                  maxCol = Math.max(maxCol, c);
                }
              }

              const filledRowCount = maxRow + 1;
              const filledColCount = maxCol + 1;
              const colLetter = this.getExcelColumnLetter(filledColCount)
              const limitedRange = XLSX.utils.decode_range(`A1:${colLetter}${filledRowCount}`);
              const jsonData = XLSX.utils.sheet_to_json(worksheet, { defval: '',range : limitedRange });
              
              return jsonData;
            }
        ),
        catchError(error => {
          console.error('Error details:', error);
          return of(null);
        })
    );
  }

  /**
   * Converts Excel data to structured validation configuration format
   * - Groups validation rules by field and form
   * - Processes validation types, error messages, and conditions
   * - Organizes validation data for efficient lookup
   * 
   * @param excelData Raw data from Excel file
   * @returns Structured validation configuration object
   */
  convertExcelToJson(excelData) : { [key: string]: NewValidationItem[] } {
    const groupedValidations: { [key: string]: any } = {};
    excelData.forEach(row => {
      
      const fieldName = row['Field Name'] + "->" + row['Form Name'];
      if (!groupedValidations[fieldName]) {
        groupedValidations[fieldName] = {
          fieldName: row['Field Name'],
          formName : row['Form Name'],
          fieldType : row['Field Type'],
          controlName: row['Control Name'],
          validations: {}
        };
      }

      if (row['Validation Type']) {
        groupedValidations[fieldName].validations[row['Validation Type']] = {
          enabled: row['Enabled'],
          errorMessage: row['Error Message'] || null,
          exceptionCd: row['Exception Code'] == "NULL" ? null : row['Exception Code'],
          overridable: row['Overridable'],
          type: row['Type'] == "NULL" ? null : 'WARNING',
          validationLevel: row['Validation Level'] == "NULL" ? null : row['Validation Level'],
          valueForParameter: row['Value for Parameter'] || null,
          sourceFields: [],
          conditionType: row['Condition Type'] == "NULL" ? null : row['Condition Type']
        };
      }

      if(this.commonUtilty.isArrayIterable(row['Source Fields']) && !row['Trigger Values']) {
        groupedValidations[fieldName].validations[row['Validation Type']].sourceFields = [...row['Source Fields']];
      } else if(row['Source Fields'] && row['Source Fields']!="NULL" && row['Trigger Values'] && row['Trigger Values']!="NULL") {
        let sourceFieldObject : any = {
          field: row['Source Fields'],
          triggerValue: typeof row['Trigger Values']=='string' ? row['Trigger Values'].split(',').map(val => val.trim()) : [row['Trigger Values']]
        };
        groupedValidations[fieldName].validations[row['Validation Type']].sourceFields.push(sourceFieldObject);
      }

    });
    
    const validationsByForm : { [key: string]: NewValidationItem[] } = {};
    Object.values(groupedValidations).forEach(validation => {
      const formName = validation.formName || 'default';
      if (!validationsByForm[formName]) {
        validationsByForm[formName] = [];
      }
      validationsByForm[formName].push(validation);
    });
  
    return validationsByForm;
  }

  /**
   * Finds and processes all invalid fields in a form
   * @param Form The form group to check
   * @param formName Name of the form
   * @param componentName Name of the component containing the form
   * @returns Array of ErrorDO objects for invalid fields
   */
  public checkInvalidAndEmptyFields(Form: FormGroup, formName, componentName) {
    let invalidFields = [];
    if (!Form.valid) {
      for (const i in Form.controls) {
        let controlName = this.commonUtilty.getControlName(Form.controls[i]);
        let validationErrorList = this.getUIErrorList(Form.get(controlName), formName);
        if (Form.controls[i].status === 'INVALID') {
          validationErrorList.forEach(validationError => {
            let errorDO = new ErrorDO();
            errorDO.errorMsg = validationError.errorMessage;
            errorDO.validationLevel = validationError.validationLevel;
            errorDO.fieldName = controlName;
            errorDO.fieldNameDescription = validationError.fieldNameDescription;
            errorDO.componentName = componentName;
            invalidFields.push(errorDO);
          });
        }
      }
    }
    return invalidFields;
  }

  /**
   * Validates a form and returns a structured validation result
   * @param form The form group to validate
   * @param formName Name of the form
   * @param componentName Name of the component containing the form
   * @returns ValidationOutput with error status and list of errors
   */
  validateOutput(form: FormGroup, formName: string, componentName: string) {
    if(!form) return {isInvalid: false, errorDOs: []};
    this.commonUtilty.markAllFieldDirty(form);
    let validationOutput: ValidationOutput = {
      isInvalid: null,
      errorDOs: []
    };
    validationOutput.errorDOs = this.checkInvalidAndEmptyFields(form, formName, componentName);
    validationOutput.isInvalid = validationOutput.errorDOs.length > 0;
    if(validationOutput.isInvalid) {
      // Mark all invalid fields as red
      Object.keys(form.controls).forEach(key => {
        const control = form.get(key);
        if (control && control.invalid) {
          control.markAsDirty();
          control.markAsTouched();
          control.updateValueAndValidity({emitEvent : false});
        }
      });
      // Scroll to the first invalid field for better UX
      setTimeout(() => {
        const firstInvalidElement = document.querySelector('.ng-invalid');
        if (firstInvalidElement) {
          firstInvalidElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
    }
    return validationOutput;
  }

  /**
   * Gets the validation parameter value for a specific field and validation type
   * @param fieldName Name of the field
   * @param validationType Type of validation
   * @param formName Name of the form
   * @param state Additional state context (optional)
   * @returns Validation parameter value or null
   */
  setValidationValue(fieldName, validationType, formName, state?) {
    const formValidations = this.allValidations[formName];
    if (!formValidations) return null;
    
    let _filteredValidationItem;
    if (state) {
      _filteredValidationItem = formValidations.find(validation => 
        validation.fieldName === fieldName && validation.state === state && 
        validation['validations']?.[validationType] && 
        validation['validations']?.[validationType]['enabled']);
    } else {
      _filteredValidationItem = formValidations.find(validation => 
        validation.fieldName === fieldName && 
        validation['validations']?.[validationType] && 
        validation['validations']?.[validationType]['enabled']);
    }

    if (_filteredValidationItem && _filteredValidationItem.valueForParameter) {
      return parseInt(_filteredValidationItem.valueForParameter);
    }
    return null;
  }

  /**
   * Gets the error message for a required field
   * @param fieldName Name of the field
   * @param formName Name of the form
   * @param state Additional state context (optional)
   * @returns Error message or null
   */
  getErrorMsg(fieldName, formName, state?) {
    const formValidations = this.allValidations[formName];
    if (!formValidations) return null;
    
    let _filteredValidationItem;
    if (state) {
      _filteredValidationItem = formValidations.find(validation => 
        validation.fieldName === fieldName && validation.state === state && 
        validation['validations']?.['required'] && 
        validation['validations']?.['required']['enabled']);
    } else {
      _filteredValidationItem = formValidations.find(validation => 
        validation.fieldName === fieldName && 
        validation['validations']?.['required'] && 
        validation['validations']?.['required']['enabled']);
    }
    
    if (_filteredValidationItem && _filteredValidationItem.errorMessage) {
      return _filteredValidationItem.errorMessage;
    }

    return null;
  }

  /**
   * Gets all UI errors for a form control
   * @param fieldFormControl The form control to check
   * @param formName Name of the form
   * @param state Additional state context (optional)
   * @returns Array of error objects with messages and metadata
   */
  getUIErrorList(fieldFormControl, formName?, state?) {
    var controlName = this.commonUtilty.getControlName(fieldFormControl);
    if (controlName == "illnessCd" && state == "BSLI")
      controlName = "illnessCd_BSLI";
    let errorlist = this._getErrorMessageList(fieldFormControl.errors, controlName, formName, state);
    return errorlist;
  }

  /**
   * Creates a validator function based on validation type and parameters
   * @param validationName The type of validation
   * @param value Primary parameter value
   * @param secondaryValue Secondary parameter value (for range validations)
   * @param fieldName Optional field name context
   * @param formName Optional form name context
   * @returns Validator function or null
   */
  private _fetchValidatorObject(validationName, value?, secondaryValue?, fieldName?, formName?) {
    // Native Angular Validations
    if (validationName === 'required')
      return Validators.required;
    if (validationName === 'requiredTrue')
      return Validators.requiredTrue;
    if (validationName === 'email')
      return Validators.email;
    if (validationName === 'min' && value)
      return Validators.min(value);
    if (validationName === 'max' && value)
      return Validators.min(value);
    if (validationName === 'minlength' && value)
      return Validators.minLength(value);
    if (validationName === 'maxlength' && value)
      return Validators.maxLength(value);
    if (validationName === 'pattern' && value)
      return Validators.pattern(value);
   
    // Custom Validations
    if (validationName === 'digits')
      return CustomValidators.digits;
    if (validationName === 'number')
      return CustomValidators.number;
    if (validationName === 'url')
      return CustomValidators.url;
    if (validationName === 'date')
      return CustomValidators.date;
    if (validationName === 'dateISO')
      return CustomValidators.dateISO;
    if (validationName === 'creditCard')
      return CustomValidators.creditCard;
    if (validationName === 'json')
      return CustomValidators.json;
    if (validationName === 'base64')
      return CustomValidators.base64;

    if (validationName === 'minDate' && value) {
      if (value === 'today') {
        return CustomValidators.minDate(new Date());
      } else if (value === 'tomorrow') {
        var today = new Date();
        var tomorrow = new Date();
        tomorrow.setDate(today.getDate() + 1);
        return CustomValidators.minDate(tomorrow);
      } else {
        return CustomValidators.minDate(value);
      }
    }
   
    if (validationName === 'maxDate' && value) {
      if (value === 'today') {
        return CustomValidators.maxDate(new Date());
      } else if (value === 'tomorrow') {
        var today = new Date();
        var tomorrow = new Date();
        tomorrow.setDate(today.getDate() + 1);
        return CustomValidators.maxDate(tomorrow);
      } else {
        return CustomValidators.maxDate(value);
      }
    }
  
    if (validationName === 'uuid' && value)
      return CustomValidators.uuid(value);
    if (validationName === 'equal' && value)
      return CustomValidators.equal(value);
    if (validationName === 'notEqual' && value)
      return CustomValidators.notEqual(value);
    if (validationName === 'equalTo' && value)
      return CustomValidators.equalTo(value);
    if (validationName === 'notEqualTo' && value)
      return CustomValidators.notEqualTo(value);
    if (validationName === 'arrayLength' && value)
      return CustomValidators.arrayLength(value);
    if (validationName === 'greaterThan' && value)
      return CustomValidators.gt(value);
    if (validationName === 'lessThan' && value)
      return CustomValidators.lt(value);
    if (validationName === 'range' && value && secondaryValue != null)
      return CustomValidators.range([value, secondaryValue]);
    if (validationName === 'rangeLength' && value && secondaryValue != null)
      return CustomValidators.rangeLength([value, secondaryValue]);

    // Custom application validators
    if (validationName && this.customValidatorsService.validatorFunctions[validationName]) {
      return this.customValidatorsService.validatorFunctions[validationName];
    }
  }

  /**
   * Generates error message list from validation errors
   * @param errors The errors object from form control
   * @param fieldName Name of the field
   * @param formName Name of the form
   * @param state Additional state context (optional)
   * @returns Array of error message objects
   */
  _getErrorMessageList(errors, fieldName, formName?, state?) {
    let listOfMessages = [];
    errors = errors || {};
    
    const formValidations = this.allValidations[formName];
    if (!formValidations) return listOfMessages;
    
    formValidations.forEach(validationItem => {
      if (validationItem.fieldName === fieldName) {
        if (validationItem.validations) {
          Object.entries(validationItem.validations).forEach(([validationType, validationConfig]) => {
            if (errors[validationType] && validationConfig.enabled) {
              listOfMessages.push({
                fieldName: validationItem.fieldName,
                controlName: validationItem['controlName'],
                tabIndex: validationItem['tabIndex'],
                errorMessage: validationConfig.errorMessage,
                validationLevel: validationItem['validationLevel'] || 'all',
                overridable: validationItem['overridable'],
              });
            }
          });
        }
      }
    });

    return listOfMessages;
  }

  /**
   * Handles cross-field validation dependencies when a field value changes
   * @param form The form group containing the fields
   * @param formName Name of the form
   * @param fieldName Name of the field that changed
   * @param newValue The new value of the changed field
   */
  validateCrossFieldDependencies(form, formName, fieldName, newValue) {
    let sourceFieldDependencies: string[] = this.sourceFieldtoMainFieldMap[formName + fieldName];
    if (!sourceFieldDependencies) return;
    
    const formValidations = this.allValidations[formName].filter(validation => 
      sourceFieldDependencies.includes(validation.fieldName));
    if (!formValidations) return;
    
    formValidations.forEach(validationItem => {
      let targetField: FormControl = form.get(validationItem.fieldName);
      if (!targetField) return;
      
      targetField.clearValidators();
      let targetFieldValidators = [];
      
      Object.entries(validationItem.validations).forEach(([validationType, validationConfig]) => {
        let shouldApplyValidation: boolean = false;
        let isSourceFieldPresent: boolean = false;
        
        if (validationConfig.sourceFields && validationConfig.sourceFields.length > 0) {
          isSourceFieldPresent = validationConfig.sourceFields.find(field => field.field == fieldName) != undefined;
          
          if (isSourceFieldPresent && validationConfig.conditionType && validationConfig.conditionType !== '') {
            shouldApplyValidation = validationConfig.conditionType === "AND";
            
            validationConfig.sourceFields.forEach(sourceField => {
              if (sourceField.field == fieldName) {
                let match = sourceField.triggerValue.includes(newValue);
                if (validationConfig.conditionType === "AND") {
                  shouldApplyValidation = shouldApplyValidation && match;
                } else {
                  shouldApplyValidation = shouldApplyValidation || match;
                }
              }
            });
          }
          
          if (shouldApplyValidation) {
            validationConfig.enabled = true;
            const newValidator = this._fetchValidatorObject(validationType, validationConfig.valueForParameter);
            targetFieldValidators.push(newValidator);
          } else {
            validationConfig.enabled = false;
          } 
        }
      });
      
      targetField.setValidators(targetFieldValidators);
      targetField.updateValueAndValidity({ emitEvent: false });
    });
  }

  /**
   * Gets all validators for a specific field
   * @param fieldName Name of the field
   * @param formName Name of the form
   * @returns Array of validator functions or null
   */
  getValidatorsForField(fieldName, formName) {
    const formValidations = this.allValidations[formName];
    if (!formValidations) return null;
    
    let validators = [];
    let validationItem = formValidations.find(validation => validation.fieldName === fieldName);
    
    if (validationItem && validationItem.validations) {
      Object.entries(validationItem.validations).forEach(([validationType, validationConfig]) => {
        if (validationConfig.enabled) {
          let valueForParameter = ["minlength", "maxlength"].includes(validationType)
            ? parseInt(validationConfig.valueForParameter)
            : validationConfig.valueForParameter;

          let validatorFn = this._fetchValidatorObject(validationType, valueForParameter);
          if (validatorFn) validators.push(validatorFn);
        }
      });
    }
    
    return validators.length ? validators : null;
  }

  /**
   * Sets the validation schema from an external source
   * @param schema Validation schema object
   */
  setValidationSchema(schema: any) {
    this.validationSchema = this.allValidations;
  }

  /**
   * Sets the language for error messages
   * @param lang Language code
   */
  setLanguage(lang: string) {
    this.selectedLanguage = lang;
  }

  /**
   * Gets the error message for a field in the selected language
   * @param formName Name of the form
   * @param fieldName Name of the field
   * @param control The form control
   * @returns Localized error message
   */
  getErrorMessage(formName: string, fieldName: string, control: AbstractControl): string {
    if (!this.allValidations[formName][fieldName] || !this.allValidations[formName][fieldName].messages) return '';
    const messages = this.allValidations[formName][fieldName].messages[this.selectedLanguage] || {};
    
    if (control.hasError('required')) return messages.required || 'This field is required.';
    if (control.hasError('maxLength')) return messages.maxLength || 'Exceeds maximum length.';
    if (control.hasError('pattern')) return messages.pattern || 'Invalid format.';
    return '';
  }

  /**
   * Creates error data objects from validation error list
   * @param validationErrorList List of validation errors
   * @returns Array of ErrorDO objects
   */
  createNewException(validationErrorList) {
    let invalidFields = [];
    if (this.commonUtilty.isArrayIterable(validationErrorList)) {
      validationErrorList.forEach(validationItem => {
        let errorDO = new ErrorDO();
        errorDO.controlName = validationItem.fieldName;
        errorDO['tabIndex'] = validationItem.tabIndex;
        errorDO.errorMsg = validationItem.errorMessage;
        errorDO.validationLevel = validationItem.validationLevel;
        errorDO.fieldName = validationItem.controlName;
        errorDO.fieldNameDescription = validationItem.controlName;
        errorDO.componentName = null;
        invalidFields.push(errorDO);
      });
    }
    return invalidFields;
  }

  /**
   * Fetches all exceptions from the server
   * @returns Observable with exception data
   */
  fetchAllExceptions() {
    let url = `${environment.mono ? environment.monoMasterTokenServerContext : 'master-service'}/master/exception/fetchAll`;
    return this.apiRequest.get(url);
  }

  /**
   * Transforms server exception data to UI validation format
   * Updates error messages and exception properties in validation configuration
   */
  transformServerExceptionDOtoUIValidationDO() {
    if (!this.commonUtilty.isArrayIterable(this.appMetadataHandler.exceptionMasterCache) || !this.allValidations) {
      return;
    }
  
    Object.entries(this.allValidations).forEach(([formName, formValidations]: any) => {
      if (!Array.isArray(formValidations)) {
        return;
      }
      
      formValidations.forEach((validationItem, index) => {
        if (!validationItem.validations) {
          return;
        }
      
        Object.entries(validationItem.validations).forEach(([validationType, validationConfig]: any) => {
          if (!validationConfig || !validationConfig.exceptionCd) {
            return;
          }
          
          let foundException = this.appMetadataHandler.exceptionMasterCache.find(
            item => item.exceptionCd == validationConfig.exceptionCd && item.exceptionType == 'ERROR'
          );
          
          if (foundException) {
            const exceptionMsg = foundException.exceptionMsgDO?.find(
              item => item.languageCd == this.appMetadataHandler.defaultLanguage
            );
            
            this.allValidations[formName][index]['validations'][validationType].errorMessage = 
              exceptionMsg ? exceptionMsg.message : validationConfig.errorMessage;
            this.allValidations[formName][index]['validations'][validationType].overridable = 
              foundException.overridable;
            this.allValidations[formName][index]['validations'][validationType].type = 
              foundException.type;
            this.allValidations[formName][index]['validations'][validationType].validationLevel = 
              foundException.validationLevel;
          }
        });
      });
    });
    
    console.log("final UI validations list", this.allValidations);
  }
}
